import { FC, useCallback, useMemo, useState } from "react";
import { MDSButton, MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";
import { useAppStore } from "@/store";
import { electronModule } from "@/modules/electron";
import { RecallPermissionKind } from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { sleep } from "@/domains/event-loop/synchronization";
import {
  ConfigurationList,
  ConfigurationListItem,
  ConfigurationListItemKind,
  ConfigurationPermissionStatus,
} from "@/components/configuration";
import {
  FooterContainer,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/onboarding-steps.styles";
import { OnboardingStepper } from "@/components/onboarding/components/welcome-wizard/shared";
import styled from "@emotion/styled";
import { ItemListRowType } from "@/components/item-list/types";
import { ItemListSectionHeaderRow } from "@/components/item-list/rows/variants/item-list-section-header-row";

type Props = {
  onNext: ({ autoMeetingDetectionEnabled }: { autoMeetingDetectionEnabled: boolean }) => void;
  showFinish: boolean;
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  platform: {
    isMacOs: boolean;
    isIntelMacDesktop: boolean;
  };
};

export const OnboardingMeetingsPermissionsSetup: FC<Props> = ({
  onNext,
  showFinish,
  resolveConfigurationStatus,
  handleEnablePermission,
  handleOpenPermissionPane,
  platform: { isMacOs, isIntelMacDesktop },
}) => {
  const { store } = useAppStore();
  const [autoMeetingDetectionEnabled, setAutoMeetingDetectionEnabled] = useState(true);
  const [remindMeToJoinMeetingsAndTakeNotesEnabled, setRemindMeToJoinMeetingsAndTakeNotesEnabled] =
    useState(true);
  const [nextLoading, setNextLoading] = useState(false);

  const accessibilityEnabled =
    resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }) ===
    ConfigurationPermissionStatus.Granted;
  const ignorePermissions = !isMacOs || isIntelMacDesktop;
  const isNextDisabled = !ignorePermissions && autoMeetingDetectionEnabled && !accessibilityEnabled;

  const handleNext = useCallback(async () => {
    setNextLoading(true);

    try {
      await electronModule.setMeetingNotificationEnabled({ enabled: autoMeetingDetectionEnabled });
      store.systemPermissions.setMeetingNotificationEnabled({
        enabled: autoMeetingDetectionEnabled,
      });

      await electronModule.setCalendarNotificationEnabled({
        enabled: remindMeToJoinMeetingsAndTakeNotesEnabled,
      });
      store.systemPermissions.setCalendarNotificationEnabled({
        enabled: remindMeToJoinMeetingsAndTakeNotesEnabled,
      });

      // We include a short sleep to make the loading state more visible/less flashing
      await sleep(1000);
    } catch (error) {
      logger.error({
        message: "[OnboardingMeetingsPermissionsSetup] Failed to enable meeting notifications",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    } finally {
      setNextLoading(false);
    }

    onNext({ autoMeetingDetectionEnabled });
  }, [
    autoMeetingDetectionEnabled,
    remindMeToJoinMeetingsAndTakeNotesEnabled,
    onNext,
    store.systemPermissions,
  ]);

  const items = useMemo<ConfigurationListItem[]>(() => {
    if (isMacOs && isIntelMacDesktop) return [];

    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Toggle,
        id: "detect-unscheduled-meetings",
        title: (
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Medium}>
            Detect unscheduled meetings and remind me to have Mem take notes
          </MDSText>
        ),
        checked: autoMeetingDetectionEnabled,
        onChange: setAutoMeetingDetectionEnabled,
      },
    ];

    // Windows doesn't need special permissions.
    if (!isMacOs || !autoMeetingDetectionEnabled) return itemList;

    itemList.push(
      { kind: ConfigurationListItemKind.Divider, id: "divider-accessibility" },
      {
        kind: ConfigurationListItemKind.Permission,
        id: "accessibility",
        title: "Accessibility",
        description: "Mem relies on accessibility features to detect when you've joined a meeting",
        status: resolveConfigurationStatus({ kind: RecallPermissionKind.Accessibility }),
        onEnable: () =>
          handleEnablePermission({
            kind: RecallPermissionKind.Accessibility,
          }),
        onOpenSystemPreferences: () =>
          handleOpenPermissionPane({
            kind: RecallPermissionKind.Accessibility,
          }),
      }
    );

    return itemList;
  }, [
    autoMeetingDetectionEnabled,
    isMacOs,
    isIntelMacDesktop,
    resolveConfigurationStatus,
    handleEnablePermission,
    handleOpenPermissionPane,
  ]);

  const calendarEventItems = useMemo<ConfigurationListItem[]>(() => {
    const itemList: ConfigurationListItem[] = [
      {
        kind: ConfigurationListItemKind.Toggle,
        id: "remind-me-to-join-meetings-and-take-notes",
        title: "Remind me to join meetings and take notes as they begin",
        checked: remindMeToJoinMeetingsAndTakeNotesEnabled,
        onChange: setRemindMeToJoinMeetingsAndTakeNotesEnabled,
      },
    ];

    return itemList;
  }, [remindMeToJoinMeetingsAndTakeNotesEnabled, setRemindMeToJoinMeetingsAndTakeNotesEnabled]);

  return (
    <WizardContentWrapper>
      <StepTitle>Set up meeting notifications</StepTitle>
      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Mem will help you jump into meetings and take notes for you any time a meeting begins
        </MDSText>

        <SectionHeaderRow
          data={{
            type: ItemListRowType.SectionHeader,
            key: "calendar-events",
            payload: { title: "Calendar events" },
          }}
        />
        <ConfigurationList items={calendarEventItems} />

        {items.length > 0 && (
          <>
            <SectionHeaderRow
              data={{
                type: ItemListRowType.SectionHeader,
                key: "unscheduled-meetings",
                payload: { title: "Unscheduled meetings" },
              }}
            />
            <ConfigurationList items={items} />
          </>
        )}
      </StepContent>
      <FooterContainer align="flex-end">
        <OnboardingStepper />
        <MDSButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleNext}
          isDisabled={isNextDisabled}
          isLoading={nextLoading}
        >
          {showFinish ? "Finish" : "Next"}
        </MDSButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};

const SectionHeaderRow = styled(ItemListSectionHeaderRow)`
  padding-bottom: ${({ theme }) => theme.spacing["0"]};
  padding-inline: 0;
  padding-top: 0;
`;
